import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, ModalContent, useDisclosure } from '@nextui-org/react';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';
import { ArrowLeft, Check, ChevronRight, Crown, Dumbbell, ExternalLink, TriangleAlert } from 'lucide-react';

import { GET_WORKOUTS, GET_ME, GET_SPLIT_EXERCISES } from '../graphql/queries';
import { CREATE_WORKOUT } from '../graphql/mutations';
import { trainingNames } from '../utils/templates';
import { trackPageView, trackEvent } from '../utils/gt';
import { formatSeconds } from '../utils/workoutStats';
import { trialContext } from '../utils/subscription';
import { isProAccount } from '../utils/workoutLock';
import { freeLetters, howToSteps, spanOf, summarizeWorkout, workoutMuscles } from '../utils/splitReview';

import saveAiSplitVidEn from '../assets/pro-only-vids/save-ai-split-en.mp4';
import saveAiSplitVidBr from '../assets/pro-only-vids/save-ai-split-br.mp4';
import { ModalUpgradePro } from '../components/ModalUpgradePro';
import '../styles/pages/divisao-alexa.css';
import { PreviewArt } from '../components/app/PreviewArt';

const THUMBS_IN_ROW = 4;

// The art is drawn on white to multiply onto a light tile; a dumbbell stands in when there is none or it fails
const ExerciseArt = ({ src, className = '' }) => {
  const [hasImage, setHasImage] = useState(!!src);
  return (
    <span className={`mt-dv-art ${className}`}>
      {hasImage ? <img src={src} alt="" loading="lazy" onError={() => setHasImage(false)} /> : <Dumbbell className="mt-dv-art__none" aria-hidden="true" />}
    </span>
  );
};

// NextUI drops aria-labelledby without its own ModalHeader, so each dialog is named with aria-label
const ConfirmDialog = ({ isOpen, title, text, cancel, confirm, confirmIcon, onCancel, onConfirm }) => (
  <Modal
    isOpen={isOpen}
    aria-label={title}
    onOpenChange={(open) => !open && onCancel()}
    placement="center"
    hideCloseButton
    classNames={{ base: 'mt-root mt-dialog', backdrop: 'mt-dialog__backdrop' }}
  >
    <ModalContent>
      {() => (
        <div className="mt-dialog__body">
          <h2 className="mt-dialog__title">{title}</h2>
          <p className="mt-dialog__text">{text}</p>
          <div className="mt-dialog__actions">
            <button type="button" className="mt-btn mt-btn--quiet" onClick={onCancel}>
              {cancel}
            </button>
            <button type="button" className="mt-btn mt-btn--primary" onClick={onConfirm}>
              {confirm}
              {confirmIcon}
            </button>
          </div>
        </div>
      )}
    </ModalContent>
  </Modal>
);

/**
 * One workout opened: its muscles, the totals and the exercises as a gym sheet (a set table on desktop, rows with
 * the dose on phones). A row opens the exercise's video and steps
 */
const WorkoutDetail = ({ workout, summary, letter, muscles, library, formatNumber, onBack, backRef }) => {
  const { t } = useTranslation();
  const review = (key, options) => t(`pages.SplitReview.${key}`, options);
  const [expanded, setExpanded] = useState(-1);
  const [failedVideos, setFailedVideos] = useState({});

  const load = (sets) => {
    const weights = sets.map((set) => set.weight);
    return weights.some((weight) => weight > 0) ? { value: spanOf(weights, formatNumber), unit: sets[0].unit } : null;
  };
  const warmupText = (sets) =>
    [
      ...new Set(
        sets.map((set) => (set.weight > 0 ? `${set.reps} × ${formatNumber(set.weight)} ${set.unit}` : review('repsOnly', { count: set.reps })))
      ),
    ].join(', ');

  return (
    <>
      {onBack && (
        <div className="mt-dv-detail__bar">
          <button type="button" ref={backRef} className="mt-editor-bar__back" onClick={onBack}>
            <ArrowLeft aria-hidden="true" />
            {review('backToSplit')}
          </button>
        </div>
      )}
      <div className="mt-dv-detail__inner">
        <div className="mt-dv-detail__title-row">
          <h2 className="mt-dv-detail__title">{workout.label}</h2>
          {letter && <span className="mt-dv-pill">{review('becomes', { letter })}</span>}
        </div>
        {muscles.length > 0 && <p className="mt-dv-detail__muscles">{muscles.map((muscle) => t(`exerciseFilters.muscleGroups.${muscle}`)).join(' · ')}</p>}

        <dl className="mt-dv-stats">
          <div>
            <dt>{review('statExercises', { count: summary.exerciseCount })}</dt>
            <dd>{summary.exerciseCount}</dd>
          </div>
          <div>
            <dt>{review('statSets', { count: summary.setCount })}</dt>
            <dd>{summary.setCount}</dd>
          </div>
          <div>
            <dt>{review('statMinutes')}</dt>
            <dd>
              ~{summary.minutes}
              <small>{review('minUnit')}</small>
            </dd>
          </div>
        </dl>

        <table className="mt-dv-sheet">
          <thead>
            <tr>
              <th scope="col">
                <span className="mt-sr-only">#</span>
              </th>
              <th scope="col" className="mt-dv-sheet__left">
                {review('colExercise')}
              </th>
              <th scope="col">{review('colSets')}</th>
              <th scope="col">{review('colReps')}</th>
              <th scope="col">{review('colLoad')}</th>
              <th scope="col">{review('colRest')}</th>
              <th scope="col">
                <span className="mt-sr-only">{review('howTo')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {summary.groups.map((group, index) => {
              const isOpen = expanded === index;
              const reps = spanOf(group.working.map((set) => set.reps));
              const weight = load(group.working);
              const rest = group.working[0].rest;
              const libraryEntry = library[group.exerciseId];
              const steps = howToSteps(group.howTo);
              const detailId = `mt-dv-ex-${index}`;
              const dose = [
                `${group.working.length} × ${reps}`,
                weight && `${weight.value} ${weight.unit}`,
                rest != null && review('rest', { rest: formatSeconds(rest) }),
              ]
                .filter(Boolean)
                .join(' · ');
              const toggle = () => {
                if (!isOpen) trackEvent('split_review_view_exercise', `Split Review - View Exercise: ${group.name}`, 'split_review', true);
                setExpanded(isOpen ? -1 : index);
              };
              return (
                <Fragment key={`${index}-${group.name}`}>
                  {/* The button in the name cell is the control; a click anywhere on the row opens it too */}
                  <tr className={`mt-dv-ex${isOpen ? ' is-open' : ''}`} onClick={toggle}>
                    <td className="mt-dv-ex__num">{index + 1}</td>
                    <td className="mt-dv-ex__main">
                      <button
                        type="button"
                        className="mt-dv-ex__toggle"
                        aria-expanded={isOpen}
                        aria-controls={detailId}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggle();
                        }}
                      >
                        <ExerciseArt src={group.thumbnailUrl} />
                        <span className="mt-dv-ex__text">
                          <span className="mt-dv-ex__name">{group.name}</span>
                          <span className="mt-dv-ex__dose">{dose}</span>
                          {libraryEntry && (
                            <span className="mt-dv-ex__meta">
                              {[
                                libraryEntry.muscleGroup && t(`exerciseFilters.muscleGroups.${libraryEntry.muscleGroup}`),
                                libraryEntry.equipmentCategory && t(`exerciseFilters.equipmentCategories.${libraryEntry.equipmentCategory}`),
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          )}
                          {group.warmups.length > 0 && (
                            <span className="mt-dv-ex__warmup">{review('warmups', { count: group.warmups.length, sets: warmupText(group.warmups) })}</span>
                          )}
                        </span>
                        <ChevronRight className="mt-dv-ex__chevron" aria-hidden="true" />
                      </button>
                    </td>
                    <td className="mt-dv-ex__n">{group.working.length}</td>
                    <td className="mt-dv-ex__n">{reps}</td>
                    <td className="mt-dv-ex__n">
                      {weight ? (
                        <>
                          {weight.value} <span className="mt-dv-ex__unit">{weight.unit}</span>
                        </>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td className="mt-dv-ex__n">{rest != null ? formatSeconds(rest) : '–'}</td>
                    <td className="mt-dv-ex__end" aria-hidden="true">
                      <ChevronRight />
                    </td>
                  </tr>
                  <tr className="mt-dv-more" id={detailId} hidden={!isOpen}>
                    <td colSpan={7}>
                      {isOpen && (
                        <div className="mt-dv-more__inner">
                          <span className="mt-dv-art mt-dv-more__media">
                            {group.videoUrl && !failedVideos[index] ? (
                              <video
                                src={group.videoUrl}
                                poster={group.thumbnailUrl || undefined}
                                autoPlay
                                loop
                                muted
                                playsInline
                                onError={() => setFailedVideos((current) => ({ ...current, [index]: true }))}
                              />
                            ) : group.thumbnailUrl ? (
                              <img src={group.thumbnailUrl} alt="" />
                            ) : (
                              <Dumbbell className="mt-dv-art__none" aria-hidden="true" />
                            )}
                          </span>
                          {steps.length > 0 && (
                            <div>
                              <h3 className="mt-dv-more__title">{review('howTo')}</h3>
                              <ol className="mt-dv-steps">
                                {steps.map((step, stepIndex) => (
                                  <li key={stepIndex}>
                                    <span aria-hidden="true">{stepIndex + 1}</span>
                                    <p>{step}</p>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

/**
 * Review of an AI split before it's saved (owner's pick 29/10, "A · Índice"): the workouts as a list with the open
 * one beside it on desktop; on phones the list is the page and a workout opens as its own page (?w=<index>, so back
 * closes it). Reached only with the split in the history state (Workout.jsx onSplitReady). It says which letter
 * each workout takes and whether the split fits the free slots before saving, and leaving asks first: the split
 * cost paid AI calls and isn't kept anywhere else
 */
export const SplitReview = () => {
  const { t, i18n: { language } } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const review = (key, options) => t(`pages.SplitReview.${key}`, options);

  const generatedWorkouts = location.state?.generatedWorkouts;
  const split = useMemo(() => generatedWorkouts || [], [generatedWorkouts]);

  // { n } while saving: the workout being saved, 1-based
  const [saving, setSaving] = useState(null);
  // The letters the workouts were saved as, once they're all saved
  const [savedLetters, setSavedLetters] = useState(null);
  const [isSavedOpen, setIsSavedOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const leaving = useRef(false);
  const openedHere = useRef(false);
  const listScroll = useRef(0);
  const backRef = useRef(null);

  const {
    isOpen: isUpgradeOpen,
    onOpen: onUpgradeOpen,
    onOpenChange: onUpgradeOpenChange,
    onClose: onUpgradeClose,
  } = useDisclosure();

  const { data: dataWorkouts, refetch: refetchWorkouts } = useQuery(GET_WORKOUTS);
  const { data: dataMe } = useQuery(GET_ME);
  const exerciseIds = useMemo(
    () => [...new Set(split.flatMap((workout) => (workout.exercises || []).map((exercise) => exercise.exerciseId)).filter(Boolean))],
    [split]
  );
  const { data: dataLibrary } = useQuery(GET_SPLIT_EXERCISES, { variables: { ids: exerciseIds }, skip: !exerciseIds.length });
  const [createWorkoutMutation] = useMutation(CREATE_WORKOUT);

  const me = dataMe?.me;
  // Free only once the account is known: a Pro account never flashes the Pro card while it loads
  const isFree = !!me && !isProAccount(me);
  const isSaved = !!savedLetters;

  const summaries = useMemo(() => split.map(summarizeWorkout), [split]);
  const library = useMemo(
    () => Object.fromEntries((dataLibrary?.exercises?.data || []).map((exercise) => [exercise.attributes.exerciseId, exercise.attributes])),
    [dataLibrary]
  );
  const formatNumber = (value) => value.toLocaleString(language === 'pt' ? 'pt-BR' : 'en-US', { maximumFractionDigits: 2 });

  // The letters the split takes (the free ones, in order); unknown until the account's workouts load
  const takenNames = dataWorkouts?.workouts?.data?.map((workout) => workout.attributes.name);
  const available = takenNames ? freeLetters(trainingNames, takenNames) : null;
  const fits = !available || available.length >= split.length;
  const letterOf = (index) => (isSaved ? savedLetters[index] : available && fits ? available[index] : null);
  const slotCopy = available && {
    free: review('free', { count: available.length }),
    total: split.length,
    remove: review('workouts', { count: split.length - available.length }),
  };

  const param = searchParams.get('w');
  const paramIndex = param !== null && /^\d+$/.test(param) && Number(param) < split.length ? Number(param) : null;
  // Desktop always shows one workout (the first by default); phones open one as its own page
  const current = paramIndex ?? 0;
  const isPhonePageOpen = !isDesktop && paramIndex !== null;

  useEffect(() => {
    trackPageView('split_review', `${t('appUrl')}/split-review`, '/split-review');
    if (!generatedWorkouts) navigate('/workouts', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving with the split unsaved (back, the rail, the browser's back) asks first. Plans isn't leaving it: that's
  // how a free account saves it, and coming back returns to it
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !!generatedWorkouts && !isSaved && !leaving.current && currentLocation.pathname !== nextLocation.pathname && nextLocation.pathname !== '/plans'
  );

  useEffect(() => {
    if (!generatedWorkouts || isSaved) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [generatedWorkouts, isSaved]);

  // Short of slots, Meus treinos opens in another tab: coming back here, the slots are counted again
  useEffect(() => {
    if (fits) return undefined;
    const recount = () => refetchWorkouts();
    window.addEventListener('focus', recount);
    return () => window.removeEventListener('focus', recount);
  }, [fits, refetchWorkouts]);

  // Phones: an open workout replaces the list in the page (under the app's header and above its tab bar), from the
  // top, with the way back focused; closing it returns to where the list was
  useEffect(() => {
    if (isDesktop) return;
    if (isPhonePageOpen) {
      window.scrollTo(0, 0);
      backRef.current?.focus({ preventScroll: true });
    } else if (listScroll.current) {
      window.scrollTo(0, listScroll.current);
    }
  }, [isPhonePageOpen, isDesktop]);

  const select = (index) => {
    trackEvent('split_review_view_workout', `Split Review - View Workout: ${split[index]?.label}`, 'split_review', true);
    // On phones it's a new page in the history, so back closes it; on desktop it just changes the pane
    openedHere.current = !isDesktop;
    if (!isDesktop) listScroll.current = window.scrollY;
    setSearchParams({ w: String(index) }, { replace: isDesktop, state: location.state });
  };

  const closeWorkout = () => {
    if (openedHere.current) navigate(-1);
    else setSearchParams({}, { replace: true, state: location.state });
    openedHere.current = false;
  };

  const leaveTo = (to, options) => {
    leaving.current = true;
    navigate(to, options);
  };

  const openMyWorkouts = () => window.open('/workouts', '_blank', 'noopener');

  const handleSave = async () => {
    trackEvent('split_review_save', 'Split Review - Save', 'split_review', true);
    const availableNames = freeLetters(trainingNames, dataWorkouts?.workouts?.data?.map((workout) => workout.attributes.name) ?? []);
    // Saving only part of a split would leave it broken: say so before saving anything
    if (availableNames.length < split.length) {
      setConfirm('slots');
      return;
    }

    setSaving({ n: 1 });
    const shuffledImages = Array.from({ length: 15 }, (_, i) => `workout${i}`).sort(() => Math.random() - 0.5);
    try {
      for (let i = 0; i < split.length; i++) {
        setSaving({ n: i + 1 });
        await createWorkoutMutation({
          variables: {
            workoutInput: {
              name: availableNames[i],
              description: split[i].label ?? '',
              image: shuffledImages[i % shuffledImages.length],
              motivation: true,
              exercises: split[i].exercises,
            },
          },
        });
      }
      await refetchWorkouts();
      setSavedLetters(availableNames.slice(0, split.length));
      setIsSavedOpen(true);
    } catch (error) {
      if (/plano gratuito|free plan/i.test(error?.message ?? '')) {
        onUpgradeOpen();
      } else {
        toast.error(t('notifications.workoutSaveError'));
      }
      // Whatever was saved before the failure is real: refresh so the list shows it
      refetchWorkouts();
    }
    setSaving(null);
  };

  const handleSecondary = () => {
    if (isFree) {
      trackEvent('split_review_create_manual', 'Split Review - Create Manual Workout', 'split_review', true);
      leaveTo('/workout', { state: { skipModal: true } });
    } else if (isSaved) {
      trackEvent('split_review_generate_again', 'Split Review - Generate Again', 'split_review', true);
      navigate('/workout');
    } else {
      setConfirm('regenerate');
    }
  };

  const subscribe = () => {
    trackEvent('split_review_subscribe_to_pro', 'Split Review - Subscribe to Pro CTA', 'split_review', true);
    navigate('/plans');
  };

  if (!generatedWorkouts) return null;

  const totalSets = summaries.reduce((total, summary) => total + summary.setCount, 0);
  const totalMinutes = summaries.reduce((total, summary) => total + summary.minutes, 0);
  const totalExercises = summaries.reduce((total, summary) => total + summary.exerciseCount, 0);
  const musclesOf = (index) => workoutMuscles(summaries[index].groups, library);

  const primary = isFree ? (
    <button type="button" className="mt-btn mt-btn--primary" onClick={subscribe}>
      <Crown aria-hidden="true" />
      {review('subscribeToPro', { context: trialContext(me) })}
    </button>
  ) : (
    <button type="button" className="mt-btn mt-btn--primary" onClick={handleSave} disabled={!!saving || isSaved}>
      {saving ? (
        <>
          <span className="mt-btn__spinner" aria-hidden="true" />
          {review('saving', { n: saving.n, total: split.length })}
        </>
      ) : isSaved ? (
        <>
          <Check aria-hidden="true" />
          {review('saved')}
        </>
      ) : (
        review('save')
      )}
    </button>
  );
  const secondary = (
    <button type="button" className="mt-btn mt-btn--quiet" onClick={handleSecondary} disabled={!!saving}>
      {isFree ? review('createManual') : review('generateAgain')}
    </button>
  );

  return (
    <div className="mt-root mt-root--app mt-dv-page">
      <ModalUpgradePro
        isOpen={isUpgradeOpen}
        onOpenChange={onUpgradeOpenChange}
        onClose={onUpgradeClose}
        title={review('proRequiredBanner')}
        description={review('proRequiredDesc')}
        videoSrc={language === 'pt' ? saveAiSplitVidBr : saveAiSplitVidEn}
      />

      <ConfirmDialog
        isOpen={blocker.state === 'blocked'}
        title={review('leaveTitle')}
        text={review('leaveText')}
        cancel={review('stay')}
        confirm={review('leave')}
        onCancel={() => blocker.reset?.()}
        onConfirm={() => {
          trackEvent('split_review_leave', 'Split Review - Leave Without Saving', 'split_review', true);
          blocker.proceed?.();
        }}
      />
      <ConfirmDialog
        isOpen={confirm === 'regenerate'}
        title={review('regenerateTitle')}
        text={review('regenerateText')}
        cancel={review('cancel')}
        confirm={review('generateAgain')}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          trackEvent('split_review_generate_again', 'Split Review - Generate Again', 'split_review', true);
          leaveTo('/workout');
        }}
      />
      {slotCopy && (
        <ConfirmDialog
          isOpen={confirm === 'slots'}
          title={review('slotsDialogTitle')}
          text={review('slotsDialogText', slotCopy)}
          cancel={review('notNow')}
          confirm={review('slotsDialogOpen')}
          confirmIcon={<ExternalLink className="mt-dv-dialog-icon" aria-hidden="true" />}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            setConfirm(null);
            openMyWorkouts();
          }}
        />
      )}

      {isSaved && (
        <Modal
          isOpen={isSavedOpen}
          aria-label={review('savedTitle')}
          onOpenChange={(open) => !open && setIsSavedOpen(false)}
          placement="center"
          hideCloseButton
          classNames={{ base: 'mt-root mt-dialog', backdrop: 'mt-dialog__backdrop' }}
        >
          <ModalContent>
            {() => (
              <div className="mt-dialog__body">
                <h2 className="mt-dialog__title">{review('savedTitle')}</h2>
                <p className="mt-dialog__text">{review('savedText', { total: split.length })}</p>
                <ul className="mt-dv-saved">
                  {split.map((workout, index) => (
                    <li key={`${index}-${workout.label}`}>
                      <span className="mt-dv-saved__letter" aria-hidden="true">
                        {savedLetters[index]}
                      </span>
                      <span className="mt-dv-saved__text">
                        <b>{review('letter', { letter: savedLetters[index] })}</b>
                        <small>{[workout.label, musclesOf(index).map((muscle) => t(`exerciseFilters.muscleGroups.${muscle}`)).join(', ')].filter(Boolean).join(' · ')}</small>
                      </span>
                      <Check className="mt-dv-saved__check" aria-hidden="true" />
                    </li>
                  ))}
                </ul>
                <div className="mt-dialog__actions">
                  <button type="button" className="mt-btn mt-btn--primary" onClick={() => navigate('/workouts')}>
                    {review('savedCta')}
                  </button>
                </div>
              </div>
            )}
          </ModalContent>
        </Modal>
      )}

      {!isPhonePageOpen && (
        <>
          <header className="mt-editor-bar mt-dv-bar">
            <button type="button" className="mt-editor-bar__back" onClick={() => navigate('/workouts')}>
              <ArrowLeft aria-hidden="true" />
              <span className="mt-editor-bar__back-label">{review('back')}</span>
            </button>
            <span />
            <div className="mt-editor-bar__actions">
              <span className="mt-editor-bar__save">
                {secondary}
                {primary}
              </span>
            </div>
          </header>

          <div className="mt-dv-head">
            <h1 className="mt-dv-head__title">{review('title')}</h1>
            <p className="mt-dv-head__sub">
              {review('subtitle', { workouts: review('workouts', { count: split.length }), exercises: review('exercises', { count: totalExercises }) })}
            </p>
          </div>

          {(isFree || (!isSaved && !fits && slotCopy)) && (
            <div className="mt-dv-notes">
              {isFree && (
                <div className="mt-dv-procard">
                  <PreviewArt kind="letters" badge="lock" size="sm" />
                  <b>{review('proTitle')}</b>
                  <span>{review('proText', { total: split.length, context: trialContext(me) })}</span>
                </div>
              )}
              {!isFree && !isSaved && !fits && slotCopy && (
                <div className="mt-dv-notice" role="status">
                  <TriangleAlert aria-hidden="true" />
                  <p>
                    <b>{review('slotsTitle')}</b> {review('slotsText', slotCopy)}{' '}
                    <a href="/workouts" target="_blank" rel="noopener">
                      {review('slotsLink')}
                      <span className="mt-sr-only"> {review('newTab')}</span>
                    </a>{' '}
                    {review('slotsAfter')}
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div className={`mt-dv-layout${isPhonePageOpen ? ' is-page' : ''}`}>
        {!isPhonePageOpen && (
          <section className="mt-dv-list" aria-label={review('listLabel')}>
            <ul className="mt-dv-rows">
              {split.map((workout, index) => {
                const summary = summaries[index];
                const letter = letterOf(index);
                const muscles = musclesOf(index);
                const thumbs = summary.groups.slice(0, THUMBS_IN_ROW);
                const more = summary.groups.length - thumbs.length;
                return (
                  <li key={`${index}-${workout.label}`}>
                    <button
                      type="button"
                      className="mt-dv-row"
                      aria-current={isDesktop && index === current ? 'true' : undefined}
                      onClick={() => select(index)}
                    >
                      <span className="mt-dv-row__top">
                        <span className="mt-dv-row__name">{workout.label}</span>
                        {letter && <span className="mt-dv-pill">{review('letter', { letter })}</span>}
                      </span>
                      {muscles.length > 0 && (
                        <span className="mt-dv-row__muscles">{muscles.map((muscle) => t(`exerciseFilters.muscleGroups.${muscle}`)).join(' · ')}</span>
                      )}
                      <span className="mt-dv-row__meta">
                        {[review('exercises', { count: summary.exerciseCount }), review('sets', { count: summary.setCount }), review('minutes', { count: summary.minutes })].join(
                          ' · '
                        )}
                      </span>
                      <span className="mt-dv-row__thumbs" aria-hidden="true">
                        {thumbs.map((group, groupIndex) => (
                          <ExerciseArt key={groupIndex} src={group.thumbnailUrl} />
                        ))}
                        {more > 0 && <span className="mt-dv-row__more">+{more}</span>}
                      </span>
                      <ChevronRight className="mt-dv-row__chevron" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="mt-dv-week">
              <span>{review('week')}</span>
              <span>
                <b>{review('sets', { count: totalSets })}</b> ·{' '}
                {totalMinutes >= 60
                  ? review('weekTime', { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 })
                  : review('weekTimeShort', { minutes: totalMinutes })}
              </span>
            </p>
          </section>
        )}

        {(isDesktop || isPhonePageOpen) && (
          <article
            key={isDesktop ? 'pane' : `page-${current}`}
            className={`mt-dv-detail${isDesktop ? '' : ' mt-dv-detail--page'}`}
            aria-label={split[current].label}
          >
            <WorkoutDetail
              key={current}
              workout={split[current]}
              summary={summaries[current]}
              letter={letterOf(current)}
              muscles={musclesOf(current)}
              library={library}
              formatNumber={formatNumber}
              onBack={isDesktop ? null : closeWorkout}
              backRef={backRef}
            />
          </article>
        )}
      </div>

      <div className="mt-dv-savebar">
        {secondary}
        {primary}
      </div>
      {/* The save buttons are drawn twice (back bar on desktop, bottom bar on phones): progress is said once */}
      <p className="mt-sr-only" role="status">
        {saving ? review('saving', { n: saving.n, total: split.length }) : isSaved ? review('savedTitle') : ''}
      </p>
    </div>
  );
};
